import { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

import { useSidePanelClaim, useSidePanelSlot } from './context/SidePanelSlotContext';

import type { ReactElement } from 'react';
import type ISidePanel from './interfaces/ISidePanel';

import styles from './scss/SidePanel.module.scss';

/**
 * One panel in a screen's side-panel slot: a plain flex box, shown or hidden by `open`, sized by
 * its own `className` (a `width` is all most callers need) so the slot, and so the main content,
 * resizes to match.
 *
 * Portals into the slot rather than rendering in place, so a screen with several of these — each
 * mounted the whole time and toggling its own `open` the way this family of panels already did —
 * can drop them anywhere in its tree without them stacking on top of each other visually.
 *
 * `open` alone is not trusted to mean "shown": the slot has one occupant at a time, so this also
 * claims it the moment `open` turns true and only actually displays while it still holds that
 * claim. A caller that opens this panel without closing whichever one it is replacing still ends
 * up with the slot showing just the one it opened, rather than both stacked together.
 */
function SidePanel({
  open, children, className, role, ariaModal, ariaLabel, dataAnalysisId,
}: ISidePanel): ReactElement {
  const slot = useSidePanelSlot();
  const panelId = useId();
  const { activePanelId, claim, release } = useSidePanelClaim();

  useEffect(() => {
    if (!open) {
      release(panelId);

      return undefined;
    }

    claim(panelId);

    return () => release(panelId);
  }, [open, panelId, claim, release]);

  const isShown = open && activePanelId === panelId;

  return createPortal(
    <div
      className={clsx(styles.panel, className, { [styles.open]: isShown })}
      aria-hidden={!isShown}
      role={role}
      aria-modal={ariaModal}
      aria-label={ariaLabel}
      data-analysis-id={dataAnalysisId}
    >
      {children}
    </div>,
    slot || document.body,
  );
}

export default SidePanel;
